"use client";

import { intlTag } from "@/lib/i18n/translations";
import Link from "@/components/LocaleLink";
import Image from "next/image";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "@/lib/i18n/LanguageContext";
import { AUTHORS, aboutAuthorLabel, getAuthorLocalized, type AuthorId } from "@/lib/authors";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/Button";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { SectionHeader } from "@/components/landing/SectionHeader";
import { ArticleCard } from "../ArticleCard";
import type { Article } from "@/lib/ratgeber";

/**
 * Central DE/EN/DA copy map plus small client components for the chrome around
 * the article body (meta row, author bio, sidebar, related guides, final CTA).
 */
const CHROME = {
  de: {
    readTimeLabel: "Lesezeit",
    updatedPrefix: "Aktualisiert",
    statsEssential: "Essential, zzgl. Gebühr",
    statsEssentialValue: "€249,99",
    statsComplete: "Complete, all-in",
    statsCompleteValue: "€649,99",
    statsDuration: "Dauer",
    statsDurationValue: "meist 3–6 Wochen",
    ctaEyebrow: "Nächster Schritt",
    ctaHeadlineA: "Was braucht",
    ctaHeadlineB: "euer Fall konkret?",
    ctaBody: "Ein paar kurze Fragen, und ihr seht sofort eure Ersteinschätzung. Eure persönliche Dokumentenliste bekommt ihr danach im persönlichen Kontakt. Kostenlos.",
    ctaButton: "Kostenloser 60-Sekunden-Check",
    backToAll: "Alle Artikel",
    miniCtaTitle: "Passt Dänemark zu euch?",
    miniCtaBody: "Ein paar kurze Fragen zeigen euch, ob der dänische Weg für euch passt.",
    miniCtaPrimary: "Check starten",
    relatedEyebrow: "Weiterlesen",
    relatedTitle: "Weitere",
    relatedAccent: "Ratgeber-Artikel.",
    relatedRead: "Artikel lesen",
    dateLocale: "de-DE",
  },
  en: {
    readTimeLabel: "read",
    updatedPrefix: "Updated",
    statsEssential: "Essential, plus fee",
    statsEssentialValue: "€249.99",
    statsComplete: "Complete, all-in",
    statsCompleteValue: "€649.99",
    statsDuration: "Duration",
    statsDurationValue: "usually 3–6 weeks",
    ctaEyebrow: "Next step",
    ctaHeadlineA: "What does",
    ctaHeadlineB: "your case need?",
    ctaBody: "A few short questions and you'll see your initial assessment instantly. You then get your personal document list from us in person. Free of charge.",
    ctaButton: "Free 60-second check",
    backToAll: "All articles",
    miniCtaTitle: "Is Denmark right for you?",
    miniCtaBody: "A few short questions show you whether the Danish route is right for you.",
    miniCtaPrimary: "Start the check",
    relatedEyebrow: "Keep reading",
    relatedTitle: "More",
    relatedAccent: "articles.",
    relatedRead: "Read article",
    dateLocale: "en-GB",
  },
  da: {
    readTimeLabel: "læsetid",
    updatedPrefix: "Opdateret",
    statsEssential: "Essential, plus gebyr",
    statsEssentialValue: "€249,99",
    statsComplete: "Complete, alt inkl.",
    statsCompleteValue: "€649,99",
    statsDuration: "Varighed",
    statsDurationValue: "typisk 3–6 uger",
    ctaEyebrow: "Næste skridt",
    ctaHeadlineA: "Hvad har",
    ctaHeadlineB: "jeres sag brug for?",
    ctaBody: "Et par korte spørgsmål, og I ser straks jeres første vurdering. Jeres personlige dokumentliste får I derefter ved personlig kontakt. Gratis.",
    ctaButton: "Gratis 60-sekunders tjek",
    backToAll: "Alle artikler",
    miniCtaTitle: "Passer Danmark til jer?",
    miniCtaBody: "Et par korte spørgsmål viser jer, om den danske vej passer til jer.",
    miniCtaPrimary: "Start tjekket",
    relatedEyebrow: "Læs videre",
    relatedTitle: "Flere",
    relatedAccent: "artikler.",
    relatedRead: "Læs artikel",
    dateLocale: "da-DK",
  },
  ar: {
    readTimeLabel: "قراءة",
    updatedPrefix: "آخر تحديث:",
    statsEssential: "Essential، والرسوم إضافية",
    statsEssentialValue: "€249.99",
    statsComplete: "Complete، شاملة كل شيء",
    statsCompleteValue: "€649.99",
    statsDuration: "المدة",
    statsDurationValue: "عادةً 3–6 أسابيع",
    ctaEyebrow: "الخطوة التالية",
    ctaHeadlineA: "ما الذي",
    ctaHeadlineB: "تحتاجه حالتكم؟",
    ctaBody: "بضعة أسئلة قصيرة، وترون تقييمكم الأولي فورًا. ثم تحصلون على قائمة وثائقكم الشخصية منّا بالتواصل الشخصي. مجانًا.",
    ctaButton: "الفحص المجاني خلال 60 ثانية",
    backToAll: "جميع المقالات",
    miniCtaTitle: "هل الدنمارك مناسبة لكم؟",
    miniCtaBody: "بضعة أسئلة قصيرة تُظهر لكم ما إذا كان الطريق الدنماركي مناسبًا لكم.",
    miniCtaPrimary: "ابدأوا الفحص",
    relatedEyebrow: "تابعوا القراءة",
    relatedTitle: "مزيد من",
    relatedAccent: "المقالات.",
    relatedRead: "اقرأوا المقال",
    dateLocale: "ar-u-nu-latn",
  },
  uk: {
    readTimeLabel: "читання",
    updatedPrefix: "Оновлено",
    statsEssential: "Essential, плюс збір",
    statsEssentialValue: "€249,99",
    statsComplete: "Complete, усе включено",
    statsCompleteValue: "€649,99",
    statsDuration: "Тривалість",
    statsDurationValue: "зазвичай 3–6 тижнів",
    ctaEyebrow: "Наступний крок",
    ctaHeadlineA: "Що потрібно",
    ctaHeadlineB: "саме у вашому випадку?",
    ctaBody: "Кілька коротких запитань — і ви одразу побачите первинну оцінку. Персональний список документів ви потім отримаєте від нас особисто. Безкоштовно.",
    ctaButton: "Безкоштовна перевірка за 60 секунд",
    backToAll: "Усі статті",
    miniCtaTitle: "Чи підходить вам Данія?",
    miniCtaBody: "Кілька коротких запитань покажуть, чи підходить вам данський шлях.",
    miniCtaPrimary: "Почати перевірку",
    relatedEyebrow: "Читайте також",
    relatedTitle: "Більше",
    relatedAccent: "статей.",
    relatedRead: "Читати статтю",
    dateLocale: "uk-UA",
  },
  ru: {
    readTimeLabel: "чтения",
    updatedPrefix: "Обновлено:",
    statsEssential: "Essential, плюс сбор",
    statsEssentialValue: "€249,99",
    statsComplete: "Complete, всё включено",
    statsCompleteValue: "€649,99",
    statsDuration: "Срок",
    statsDurationValue: "обычно 3–6 недель",
    ctaEyebrow: "Следующий шаг",
    ctaHeadlineA: "Что нужно",
    ctaHeadlineB: "именно в вашем случае?",
    ctaBody: "Несколько коротких вопросов — и вы сразу увидите первичную оценку. Персональный список документов вы затем получите от нас лично. Бесплатно.",
    ctaButton: "Бесплатная проверка за 60 секунд",
    backToAll: "Все статьи",
    miniCtaTitle: "Подходит ли вам Дания?",
    miniCtaBody: "Несколько коротких вопросов покажут, подходит ли вам датский путь.",
    miniCtaPrimary: "Начать проверку",
    relatedEyebrow: "Читать дальше",
    relatedTitle: "Другие",
    relatedAccent: "статьи.",
    relatedRead: "Читать статью",
    dateLocale: "ru-RU",
  },
  vi: {
    readTimeLabel: "đọc",
    updatedPrefix: "Cập nhật",
    statsEssential: "Essential, chưa gồm lệ phí",
    statsEssentialValue: "€249,99",
    statsComplete: "Complete, trọn gói",
    statsCompleteValue: "€649,99",
    statsDuration: "Thời gian",
    statsDurationValue: "thường 3–6 tuần",
    ctaEyebrow: "Bước tiếp theo",
    ctaHeadlineA: "Trường hợp của các bạn",
    ctaHeadlineB: "cần những gì?",
    ctaBody: "Chỉ vài câu hỏi ngắn là các bạn thấy ngay kết quả đánh giá ban đầu. Sau đó, các bạn nhận danh sách giấy tờ riêng khi chúng tôi liên hệ trực tiếp. Miễn phí.",
    ctaButton: "Kiểm tra miễn phí trong 60 giây",
    backToAll: "Tất cả bài viết",
    miniCtaTitle: "Đan Mạch có phù hợp với các bạn không?",
    miniCtaBody: "Vài câu hỏi ngắn cho các bạn biết con đường kết hôn tại Đan Mạch có phù hợp với mình hay không.",
    miniCtaPrimary: "Bắt đầu kiểm tra",
    relatedEyebrow: "Đọc tiếp",
    relatedTitle: "Các bài viết",
    relatedAccent: "khác.",
    relatedRead: "Đọc bài viết",
    dateLocale: "vi-VN",
  },
  es: {
    readTimeLabel: "de lectura",
    updatedPrefix: "Actualizado en",
    statsEssential: "Essential, tasa aparte",
    statsEssentialValue: "€249,99",
    statsComplete: "Complete, todo incluido",
    statsCompleteValue: "€649,99",
    statsDuration: "Duración",
    statsDurationValue: "normalmente 3–6 semanas",
    ctaEyebrow: "Siguiente paso",
    ctaHeadlineA: "¿Qué necesita",
    ctaHeadlineB: "su caso en concreto?",
    ctaBody: "Unas pocas preguntas breves y verán al instante su primera valoración. Después recibirán su lista personal de documentos en contacto directo con nosotros. Gratis.",
    ctaButton: "Comprobación gratuita de 60 segundos",
    backToAll: "Todos los artículos",
    miniCtaTitle: "¿Dinamarca encaja con ustedes?",
    miniCtaBody: "Unas pocas preguntas breves les muestran si la vía danesa encaja con ustedes.",
    miniCtaPrimary: "Iniciar la comprobación",
    relatedEyebrow: "Sigan leyendo",
    relatedTitle: "Más",
    relatedAccent: "artículos de la Guía.",
    relatedRead: "Leer artículo",
    dateLocale: "es-ES",
  },
} as const;

type Locale = import("@/lib/i18n/translations").Locale;
// Structural type without literal strings; avoids TS conflicts between the
// three `as const` branches when picking by locale key.
type ChromeCopy = { [K in keyof (typeof CHROME)["de"]]: string };

function useChrome(): { c: ChromeCopy; locale: Locale } {
  const { locale } = useTranslation();
  const lang = locale;
  return { c: CHROME[lang], locale: lang };
}

/** Hero meta row: read time. */
export function LocalizedReadTime({ readTime }: { readTime: string }) {
  const { c, locale } = useChrome();
  // The article data says "4 Min" (German); the newer languages build the
  // phrase from the number in their own words.
  const minutes = parseInt(readTime, 10);
  if (Number.isFinite(minutes)) {
    if (locale === "ar") return <>{minutes} {minutes >= 3 && minutes <= 10 ? "دقائق" : "دقيقة"} {c.readTimeLabel}</>;
    if (locale === "uk") return <>{minutes} хв {c.readTimeLabel}</>;
    if (locale === "ru") return <>{minutes} мин {c.readTimeLabel}</>;
    if (locale === "vi") return <>{minutes} phút {c.readTimeLabel}</>;
    if (locale === "es") return <>{minutes} min {c.readTimeLabel}</>;
  }
  return <>{readTime} {c.readTimeLabel}</>;
}

/** Hero meta row: date of the last substantive change. */
export function LocalizedUpdated({ dateModified }: { dateModified: string }) {
  const { c, locale } = useChrome();
  const formatted = new Date(dateModified).toLocaleDateString(intlTag(locale), {
    month: "long",
    year: "numeric",
  });
  return <>{c.updatedPrefix} {formatted}</>;
}

/** Author bio: a home card with the round portrait, eyebrow, name, role and bio. */
export function LocalizedAuthorBio({ authorId }: { authorId: AuthorId }) {
  const { locale } = useChrome();
  const author = AUTHORS[authorId];
  const localized = getAuthorLocalized(authorId, locale);
  const label = aboutAuthorLabel(authorId, locale);
  return (
    <aside
      className="mb-12 rounded-lg border border-navy-100 bg-white p-5 shadow-[var(--shadow-soft)] sm:p-6"
      aria-label={`${label}: ${author.name}`}
    >
      <div className="flex items-center gap-4">
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full bg-cream-100 ring-1 ring-navy-100 sm:h-[72px] sm:w-[72px]">
          <Image
            src={author.imagePath}
            alt={author.name}
            fill
            sizes="72px"
            quality={92}
            className="object-cover object-[center_25%]"
          />
        </div>
        <div className="min-w-0">
          <p className="eyebrow">{label}</p>
          <p className="mt-1 font-display text-[1.2rem] font-semibold leading-tight text-ink-900">
            {author.name}
          </p>
          <p className="mt-0.5 text-[13px] text-navy-500">{localized.jobTitle}</p>
        </div>
      </div>
      <p className="mt-4 text-[14.5px] leading-[1.7] text-navy-600">{localized.bio}</p>
    </aside>
  );
}

/**
 * The one call to action at the end of every article: the home page's final
 * section with the article's words — light, centred, one red pill and a quiet
 * link to the prices. Replaces the dark box inside the text and the second
 * "Nächster Schritt" box, which asked for the same check twice.
 */
export function LocalizedEndCta() {
  const { c } = useChrome();
  return (
    <FinalCTA
      id="artikel-cta"
      eyebrow={c.ctaEyebrow}
      headlineA={c.ctaHeadlineA}
      headlineB={c.ctaHeadlineB}
      lead={c.ctaBody}
      primary={{ href: "/eignungs-check", label: c.ctaButton, track: "cta_article_bottom" }}
      petals={false}
    />
  );
}

/** "Alle Artikel" → "All articles" → "Alle artikler" back link. */
export function LocalizedBackToAll() {
  const { c } = useChrome();
  return (
    <Link
      href="/ratgeber"
      className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-navy-600 transition-colors hover:text-coral-700"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
      {c.backToAll}
    </Link>
  );
}

/**
 * Sidebar card: the free check, with the package facts that used to sit in a
 * separate box underneath (read time and category are in the hero now). The
 * button is a white pill: the header's "Jetzt buchen" is already red on every
 * screen, and the article's closing section carries the red check button. Both
 * packages are named, so "€649,99" never reads as the price of the free check.
 */
export function LocalizedSidebarMiniCta() {
  const { c } = useChrome();
  // Quiet and personal (operator, 28.09.2026: the former card with its three
  // short claims, the pill and the price list read as "überladen … nach AI"):
  // the two founders, one question, one sentence, one wine link.
  return (
    <div className="rounded-lg border border-navy-100 bg-white p-5">
      <span className="flex -space-x-2.5" aria-hidden>
        <Image
          src="/images/team-alexander-weit.jpg"
          alt=""
          width={72}
          height={72}
          sizes="36px"
          className="h-9 w-9 rounded-full object-cover object-top ring-2 ring-white"
        />
        <Image
          src="/images/team-thi-yen.jpg"
          alt=""
          width={72}
          height={72}
          sizes="36px"
          className="h-9 w-9 rounded-full object-cover object-top ring-2 ring-white"
        />
      </span>
      <p className="mt-3.5 text-balance font-display text-[1.12rem] leading-snug text-ink-900">{c.miniCtaTitle}</p>
      <p className="mt-1 text-[13.5px] leading-relaxed text-navy-600">{c.miniCtaBody}</p>
      <Link
        href="/eignungs-check"
        data-track="cta_article_sidebar"
        className="group mt-3 inline-flex items-center gap-1.5 text-[14px] font-semibold text-coral-700 transition-colors hover:text-coral-800"
      >
        {c.miniCtaPrimary}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" aria-hidden />
      </Link>
    </div>
  );
}

/** Related guides: the hub's cards under a home section header. */
export function LocalizedRelated({ related }: { related: Article[] }) {
  const { c, locale } = useChrome();
  const relatedLocalized = related.map((rel) => {
    if (locale === "de" || !rel.translations) return rel;
    const tr = rel.translations[locale];
    if (!tr) return rel;
    return {
      ...rel,
      title: tr.title,
      description: tr.description,
      category: tr.category,
    };
  });
  if (relatedLocalized.length === 0) return null;
  return (
    <section className="relative isolate border-t border-navy-100 bg-cream-100 py-10 sm:py-14">
      <div className="mx-auto max-w-[1100px] px-5 sm:px-8 lg:px-10">
        <SectionHeader eyebrow={c.relatedEyebrow} title={c.relatedTitle} accent={c.relatedAccent} />
        <div className="mt-8 grid gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {relatedLocalized.map((rel) => (
            <ArticleCard key={rel.slug} article={rel} readLabel={c.relatedRead} headingLevel="h3" />
          ))}
        </div>
      </div>
    </section>
  );
}
